import { Seat, Wind, type TSeat, type TWind } from './types';

export const DiceValues = {
  MinValue: 1,
  MaxValue: 6,
  Count: 3,
  MinTotal: 3,
  MaxTotal: 18,
} as const;

export type TDiceValues = readonly [number, number, number];

export const DicePhase = {
  DetermineDealer: 'determine-dealer',
  DetermineOpening: 'determine-opening',
  OpeningOnly: 'opening-only',
  Done: 'done',
} as const;

export type TDicePhase = (typeof DicePhase)[keyof typeof DicePhase];

export const advanceSeat = (seat: TSeat, distance: number): TSeat =>
  ((seat + ((distance % 4) + 4)) % 4) as TSeat;

export const diceTotal = (dice: TDiceValues): number => {
  if (
    dice.some(
      (value) =>
        !Number.isInteger(value) || value < DiceValues.MinValue || value > DiceValues.MaxValue
    )
  ) {
    throw new Error(`Invalid dice values: ${dice.join(',')}`);
  }
  return dice[0] + dice[1] + dice[2];
};

/**
 * 實體台麻由起算家算作 1，因此位移量是骰子總和減一。
 */
export const selectSeatByDice = (startingSeat: TSeat, dice: TDiceValues): TSeat =>
  advanceSeat(startingSeat, diceTotal(dice) - 1);

export const selectInitialDealer = (fakeEastSeat: TSeat, dice: TDiceValues): TSeat =>
  selectSeatByDice(fakeEastSeat, dice);

export const selectOpeningSeat = (dealerSeat: TSeat, dice: TDiceValues): TSeat =>
  selectSeatByDice(dealerSeat, dice);

export const seatWindsFromOpening = (openingSeat: TSeat): readonly [TWind, TWind, TWind, TWind] => {
  const winds: [TWind, TWind, TWind, TWind] = [Wind.East, Wind.East, Wind.East, Wind.East];
  const order = [Wind.East, Wind.South, Wind.West, Wind.North] as const;
  for (let distance = 0; distance < 4; distance++) {
    winds[advanceSeat(openingSeat, distance)] = order[distance]!;
  }
  return winds;
};

/**
 * 開門家右側數過骰子總和墩，從下一墩開始抓牌。
 * 回傳值採 0-based，0 表示從該牆最右側第一墩開始。
 */
export const openingStackIndex = (dice: TDiceValues): number => diceTotal(dice) % 18;

/** 72 墩環狀牌牆中真正開始抓牌的 global stack index。 */
export const wallStartStackIndex = (openingSeat: TSeat, dice: TDiceValues): number =>
  (openingSeat * 18 + diceTotal(dice)) % 72;

export const rollDice = (random: () => number): TDiceValues => {
  const oneDie = (): number => Math.floor(random() * DiceValues.MaxValue) + DiceValues.MinValue;
  return [oneDie(), oneDie(), oneDie()];
};

export const assertSeat = (value: number): TSeat => {
  if (
    value !== Seat.Self &&
    value !== Seat.Right &&
    value !== Seat.Opposite &&
    value !== Seat.Left
  ) {
    throw new Error(`Invalid seat: ${value}`);
  }
  return value;
};
